// Offline renders for numeric checks (suggestion "audio self-checks"; audio card 18): the same engine and graph on an
// OfflineAudioContext, a script of timed calls run against a simulated clock (everything is scheduled ahead, then
// rendered at once), and measurements of the result. An agent without ears can still read a duck's depth, the peak
// under the limiter or a fade's timing.
import { gainToDb } from "./dsp";
import { createAudio, type Audio, type AudioOptions } from "./engine";

/** A timed call in an offline script. */
export interface OfflineEvent {
  at: number;
  run(a: Audio): void;
}

/** What to render. */
export interface OfflineScript {
  seconds: number;
  sampleRate?: number;
  events: OfflineEvent[];
  /** The simulated frame (s) between `update` calls (default 0.05). */
  step?: number;
}

/** A rendered script: the buffer, and the engine (its stats and the director's log). */
export interface OfflineResult {
  buffer: AudioBuffer;
  audio: Audio;
}

/** Render a script on a fresh OfflineAudioContext (`make` builds it; the browser's by default). */
export async function renderOffline(o: AudioOptions, s: OfflineScript, make?: (channels: number, length: number, rate: number) => OfflineAudioContext): Promise<OfflineResult> {
  const sr = s.sampleRate ?? 44100;
  const ctx = make ? make(2, Math.ceil(s.seconds * sr), sr) : new OfflineAudioContext(2, Math.ceil(s.seconds * sr), sr);
  let t = 0;
  const audio = createAudio({ ...o, clock: () => t });
  audio.attach(ctx);
  const events = [...s.events].sort((a, b) => a.at - b.at);
  const step = s.step ?? 0.05;
  let k = 0;
  for (t = 0; t <= s.seconds + 1e-9; t += step) {
    while (k < events.length && events[k]!.at <= t + 1e-9) events[k++]!.run(audio);
    audio.update();
  }
  const buffer = await ctx.startRendering();
  return { buffer, audio };
}

/** Peak and RMS (dBFS) of a buffer between two times, over all channels. */
export function measure(b: AudioBuffer, from = 0, to = b.duration): { peakDb: number; rmsDb: number; peak: number; rms: number; over: number } {
  const a = Math.max(0, Math.floor(from * b.sampleRate));
  const z = Math.min(b.length, Math.ceil(to * b.sampleRate));
  let peak = 0;
  let sum = 0;
  let n = 0;
  let over = 0;
  for (let c = 0; c < b.numberOfChannels; c++) {
    const x = b.getChannelData(c);
    for (let i = a; i < z; i++) {
      const v = Math.abs(x[i]!);
      if (v > peak) peak = v;
      if (v >= 1) over++;
      sum += v * v;
      n++;
    }
  }
  const rms = n ? Math.sqrt(sum / n) : 0;
  return { peakDb: gainToDb(peak), rmsDb: gainToDb(rms), peak, rms, over };
}
